import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { IConfig } from '@onlyoffice/document-editor-angular';
import { DialogModule } from 'primeng/dialog';
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display';
import { environment } from '../../../../environments/environment.dev';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { IconSourceEnum } from '../../../@core/components/ui/icon/icon.component';
import { CustomMenuItem, MenuComponent } from '../../../@core/components/ui/menu/menu.component';
import { IAttachment, IDocumentVersion, IUser } from '../../../@core/models/model';
import { AuthenticationService } from '../../../@core/services/authentication.service';
import { ToastService } from '../../../@core/services/toast.service';
import { CaptureIssueComponent } from '../../../@shared/components/capture-issue/capture-issue.component';
import { OnlyOfficeDocEditorComponent } from "../../../@shared/components/only-office-doc-editor/only-office-doc-editor.component";
import { SupportTicketFormComponent } from '../../../@shared/components/support-ticket-form/support-ticket-form.component';
import { SupportTicketDialogService } from '../../../@shared/services/support-ticket-dialog.service';
import { SVG, TOP_BAR_SVG } from '../../../common/svgs';
import { IContract } from '../../models/contract-management.model';
import { ContractService } from '../../services/contracts.service';
import { JWTService } from '../../services/jwt.service';
import { VersionService } from '../../services/version.service';
import { ContractTopBarComponent, ContractTopBarView } from '../edit-contract/components/contract-top-bar/contract-top-bar.component';

@Component({
  selector: 'app-versions',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, OnlyOfficeDocEditorComponent, ButtonComponent, MenuComponent, UserNameDisplayComponent, ContractTopBarComponent, CaptureIssueComponent, SupportTicketFormComponent],
  templateUrl: './versions.component.html',
  styleUrl: './versions.component.scss'
})
export class VersionsComponent implements OnInit, OnDestroy {
  @ViewChild('captureIssue') captureIssueComponent!: CaptureIssueComponent;
  @ViewChild('supportTicketForm') supportTicketFormComponent!: SupportTicketFormComponent;

  public contractId: string = '';
  public headings: any[] = [];
  bottomBarVisible: boolean = false;
  supportTicketDialog: boolean = false;

  supportMenuItems: CustomMenuItem[] = [
    {
      icon: SVG.RAISE_A_TICKET,
      iconSource: IconSourceEnum.SVG,
      label: 'Raise a support ticket',
      callback: () => { this.openHelp(); }
    },
    {
      icon: SVG.CAPTURE_AN_ISSUE,
      iconSource: IconSourceEnum.SVG,
      label: 'Capture an issue',
      callback: () => { this.openCapture(); }
    },
  ];

  infoMenuItems: CustomMenuItem[] = [
    {
      icon: TOP_BAR_SVG.DOCUMENTATION,
      iconSource: IconSourceEnum.SVG,
      label: 'Documentation',
      callback: () => { window.open('http://help.doc.inncircles.ai/', '_blank'); }
    },
    {
      icon: TOP_BAR_SVG.WEBINARS,
      iconSource: IconSourceEnum.SVG,
      label: 'Webinars',
      callback: () => { window.open('https://inncircles.com/join-webinar/', '_blank'); }
    },
    {
      icon: TOP_BAR_SVG.WHATS_NEW,
      iconSource: IconSourceEnum.SVG,
      label: "What's New",
      callback: () => { window.open('https://inncircles.ai/blogs/', '_blank'); }
    },
  ];
  contract !: IContract;
  versions: IDocumentVersion[] = [];
  filteredVersions: IDocumentVersion[] = [];
  selectedVersion!: IDocumentVersion

  currentVersion!: IDocumentVersion

  config: IConfig = {}
  private jwtService = new JWTService();
  currentUser!: IUser | null | undefined
  isAnonymousUser: boolean = false;
  ContractTopBarView = ContractTopBarView;
  docFormat: string = 'docx'
  versionLoading: boolean = false;
  permissions: {
    canView: boolean,
    canEdit: boolean,
    canComment: boolean,
    restricted: boolean
  } = {
      canView: false,
      canEdit: false,
      canComment: false,
      restricted: true
    }

  copyToClipboard(url: string): void {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
    }
  }

  getMenuItems(version: IDocumentVersion): CustomMenuItem[] {
    return [
      {
        label: 'Copy Link',
        icon: 'pi pi-link',
        iconSource: IconSourceEnum.PRIMENG,
        callback: () => {
          this.versionService.getDocVersionURL(this.contract._id, version.version).subscribe(res => {
            if (res.success) {
              this.copyToClipboard(res.data.url);
              this.toast.success('Link Copied Successfully');
            } else {
              this.toast.error('Failed to Copy Link');
            }
          });
        }
      },
      {
        label: 'Restore Version',
        icon: 'pi pi-refresh',
        iconSource: IconSourceEnum.PRIMENG,
        callback: () => {
          this.versionService.restoreVersion(this.contract._id, version.version).subscribe(res => {
            if (res.success) {
              this.toast.success('Version Restored Successfully');
              this.fetchContract();
            }
          });
        }
      },
      {
        label: 'Download',
        icon: 'pi pi-download',
        iconSource: IconSourceEnum.PRIMENG,
        callback: () => {
          this.downloadVersion(version.version, this.versions.length - this.filteredVersions.indexOf(version));
        }
      },
      {
        label: 'Delete',
        icon: 'pi pi-trash',
        iconSource: IconSourceEnum.PRIMENG,
        callback: () => {
          this.deleteVersion(version.version);
        }
      }
    ];
  }


  editingVersion: IDocumentVersion | null = null;
  tempVersionName: string = '';

  constructor(
    private route: ActivatedRoute,
    private contractService: ContractService,
    private authService: AuthenticationService,
    private toast: ToastService,
    private versionService: VersionService,
    private supportTicketDialogService: SupportTicketDialogService,
  ) {
    this.route.queryParamMap.subscribe(params => {
      this.contractId = params.get('documentId') as string ?? ""
    });
    this.authService.currentUser.subscribe(res => {
      this.currentUser = res
      this.isAnonymousUser = res?.anonymous ?? false;
    });
  }

  ngOnInit(): void {
    this.fetchContract();;
    this.supportTicketDialogService.openDialog$.subscribe(payload => {
      if (payload.attachment && this.supportTicketFormComponent) {
        this.supportTicketFormComponent.attachmentFiles.push(payload.attachment.file);
        this.supportTicketFormComponent.attachmentPreviews.push(payload.attachment);
      }
      this.supportTicketDialog = true;
    });
  }

  ngOnDestroy(): void {
    this.config = {};
  }

  fetchContract() {
    this.versionLoading = true;
    if (this.contractId.length > 0) {
      this.contractService.fetchContract(this.contractId).subscribe((res: { data: IContract; }) => {
        this.contract = res.data;
        this.getVersions();
      });
    } else {
      this.toast.error('No contract found');
    }
  }

  getOfficeDocType(type: string) {
    switch (type) {
      case 'application/pdf':
        this.docFormat = 'pdf'
        break;
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        this.docFormat = 'docx'
        break;
      default:
        this.docFormat = 'docx'
    }
    let officeDocType = ''
    switch (this.docFormat) {
      case 'docx':
        officeDocType = 'word'
        break;
      case 'pdf':
        officeDocType = 'pdf'
        break;
      default:
        officeDocType = 'word'
    }
    return officeDocType;
  }

  getDocumentEditorConfig(key: string, url: string, officeDocType: string) {
    const tokenConfig: Partial<IConfig> = {
      document: {
        fileType: this.docFormat,
        key: this.contractId + "-" + key + '-' + Math.random().toString(36).substring(2, 15),
        title: this.contract.name,
        url: url,
        permissions: {
          chat: this.permissions.canEdit,
          comment: this.permissions.canComment,
          download: true,
          edit: this.permissions.canEdit,
          fillForms: true,
          modifyContentControl: true,
          modifyFilter: true,
          print: true,
          protect: true,
          review: this.permissions.canComment,
          reviewGroups: [],
          userInfoGroups: [],
        },
      },
      documentType: officeDocType,
      editorConfig: {
        user: {
          group: this.contractId,
          id: this.currentUser?.email,
          image: this.currentUser?.profilePicture?.url ? `${environment.s3Url}/${this.currentUser.profilePicture.url}?timestamp=${new Date().getTime()}` : '',
          name: this.currentUser?.firstName + ' ' + this.currentUser?.lastName
        },
        embedded: {
          toolbarDocked: "top",
        },
        customization: {
          toolbarHideFileName: true,
          hideRightMenu: true,
          compactToolbar: true,
          compactHeader: true,
          comments: true,
          forcesave: true,
          uiTheme: "theme-gray",
          logo: {
            image: `${environment.s3Url}/inndoc-ai/assets/images/3.png`,
          },
          zoom: -2, // Fit to width
        },
        callbackUrl: environment.apiUrl + '/public/save-contract-document' + "?contractId=" + this.contractId + "&userId=" + this.currentUser?._id,
      },
      height: "100%",
      width: "100%",
    }
    const token = this.jwtService.signToken(tokenConfig, environment.jwtSecret);
    this.config = {
      ...tokenConfig,
      token: token
    };
  }

  fetchContractURL(url?: string, key?: string) {
    if (!this.contractId) {
      this.toast.error('No document found, try uploading again!');
      return;
    }
    this.contractService.getContractURL(this.contractId).subscribe(res => {
      if (!res.success) return;
      const officeDocType = this.getOfficeDocType((this.contract.attachment as IAttachment).type);
      const fullUrl = url ?? res.data.attachment.fullUrl ?? res.data.attachment.url;
      const uniqueKey = key ?? res.data.key;
      this.getDocumentEditorConfig(uniqueKey, fullUrl, officeDocType);
    });
  }

  saveVersion() {
    this.versionService.saveVersion(this.contract._id).subscribe(res => {
      if (res.success) {
        this.toast.success('Version Saved Successfully')
      }
    })
  }

  getVersions() {
    this.versionService.getVersions(this.contractId).subscribe(res => {
      if (res.success) {
        this.versions = res.data.versions ?? [];
        this.filteredVersions = this.versions.slice(1);
        this.currentVersion = this.versions[0];
        this.versionLoading = false;
        if (this.versions.length === 0) {
          this.fetchContractURL();
        } else {
          this.showVersion(this.currentVersion);
        }
      }
    });
  }

  showVersion(item: IDocumentVersion) {
    if (!item) return;
    this.selectedVersion = item
    if (item.file && item.file.url) {
      this.versionService.getDocVersionURL(this.contract._id, item.version).subscribe(res => {
        if (res.success) {
          const officeDocType = this.getOfficeDocType((res.data.attachment as IAttachment).type);
          const url = res.data.url
          const key = res.data.key
          this.getDocumentEditorConfig(key, url, officeDocType);
        }
      })

    }
  }

  downloadDocument(uint8Array: Uint8Array, fileName: string, mimeType?: string) {
    const blob: Blob = new Blob([new Uint8Array(uint8Array)], {
      type: mimeType ? mimeType : 'application/pdf'
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = mimeType ? `${fileName}.docx` : `${fileName}.pdf`;
    document.body.append(link);
    link.click();
    link.remove();
  }


  downloadVersion(versionId: string, index: number) {
    this.versionLoading = true;
    this.versionService.downloadVersion(this.contract._id, versionId).subscribe(res => {
      if (res.success) {
        this.downloadDocument(res.data.data, `${this.contract.name} - Version${this.versions.length - index}`, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
        this.versionLoading = false;
      }
    })
  }

  deleteVersion(versionId: string) {
    this.versionService.deleteVersion(this.contract._id, versionId).subscribe(res => {
      if (res.success) {
        this.toast.success('Version Deleted Successfully')
        this.getVersions()
      }
    })
  }



  startEditing(version: IDocumentVersion, event: Event) {
    event.stopPropagation();
    this.editingVersion = version;
    this.tempVersionName = version.name;
  }

  saveVersionName(version: IDocumentVersion, event: Event) {
    event.stopPropagation();
    if (this.tempVersionName.trim() === '') {
      this.toast.error('Version name cannot be empty');
      return;
    }
    this.versionService.updateVersionName(this.contract._id, version.version, this.tempVersionName)
      .subscribe(res => {
        if (res.success) {
          this.toast.success('Version name updated successfully');
          this.getVersions();
        } else {
          this.toast.error('Failed to update version name');
        }
        this.editingVersion = null;
      });
  }

  cancelEditing(event: Event) {
    event.stopPropagation();
    this.editingVersion = null;
  }

  openHelp(): void {
    this.supportTicketDialog = true;
  }

  openCapture(): void {
    this.captureIssueComponent?.openCaptureDialog();
  }

  onSupportTicketSubmitted(): void {
    this.supportTicketDialog = false;
  }

  onSupportTicketDialogHide(): void {
    this.supportTicketDialog = false;
  }
}
